'use client';

import { useEffect } from 'react';
import { isChunkLoadError, reloadOnceForChunkError, reportError } from '@/lib/error-reporter';

/**
 * Route-level boundary. Catches render/lifecycle errors thrown anywhere below the root layout and
 * reports them — React render errors never surface via window.onerror, so before this they were
 * invisible to the admin Errors page.
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const chunkError = isChunkLoadError(error);

  useEffect(() => {
    // A deploy replaced the build this tab was loaded from: the old chunk is gone and reset()
    // would just fail again. Reload once onto the new build (the subtree is already lost).
    if (isChunkLoadError(error) && reloadOnceForChunkError()) return;
    reportError(error, {
      type: 'react-error-boundary',
      // Next's server-side error id — the only way to correlate with the server log when the
      // message is redacted in production.
      digest: error.digest,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
    });
  }, [error]);

  return (
    <div
      style={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        padding: 24,
        textAlign: 'center',
      }}
    >
      <h2 style={{ color: '#fff', fontSize: 18, fontWeight: 600, margin: 0 }}>Something went wrong</h2>
      <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 14, margin: 0, maxWidth: 400 }}>
        This section failed to load. We&apos;ve been notified.
      </p>
      <button
        onClick={chunkError ? () => window.location.reload() : reset}
        style={{
          marginTop: 6,
          padding: '10px 20px',
          borderRadius: 10,
          border: 'none',
          background: '#7C3AED',
          color: '#fff',
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Try again
      </button>
    </div>
  );
}
